import type { RoomLastMessagePreviewDto } from '../types/chat';

/**
 * 방 목록의 「마지막 메시지 미리보기」를 **무슨 글자로 보일지** — 판정은 여기 한 곳이다.
 *
 * 쓰는 곳이 둘이다(첫 화면 「최근 대화」 위젯 · 채팅방 목록). 각자 판정하면 **같은 방이
 * 두 화면에서 다른 말을 한다** — 이 레포에서 그 병을 여러 번 겪었다(상대 시각이 세 벌로
 * 갈렸던 것 · 위젯마다 다른 날짜 글자).
 *
 * ## ★★★ 「첨부만」과 「서버가 못 읽음」은 **다른 일**이다
 *
 * ```
 *   첨부만 있음       text: '' · hasAttachments: true     ⇒ 「첨부파일」   — 일어난 일
 *   서버가 못 읽음    칸 «자체»가 null                     ⇒ 「—」         — 우리가 모른다
 * ```
 * 조회가 실패해도 방 목록은 그대로 200 이고 이 칸만 null 이다. 둘을 **같게 그리면** 사람이
 * 「첨부만 있는 방」과 「서버가 못 읽은 방」을 구별하지 못한다.
 *
 * ## 글자는 «담백하게» (2026-09-23 사용자 결정)
 *
 * - **「사진을 보냈습니다」는 쓰지 않는다.** 서버는 **사진인지 문서인지 모른다**
 *   (`hasAttachments` 참/거짓뿐). 「사진」이라 적으면 엑셀·PDF 를 보낸 방에서 **거짓**이 된다.
 * - **「지워진 메시지」** 로 통일한다 — 「삭제된 메시지」와 섞어 쓰지 않는다(같은 뜻 두 벌 금지).
 * - ★ 이 글자는 **화면이 만든다**(2026-09-23 사용자 결정). 서버는 톰스톤 규칙대로
 *   `deleted:true · text:null` 만 주고 문구를 만들지 않는다 — 계약에 요청할 것이 없다.
 *   ⚠ 그 대신 **웹·앱이 다른 글자를 쓸 위험**을 안고 고른 것이다. 앱 쪽을 지을 때 이 글자에 맞춘다.
 *
 * ## ⛔ 잘림 표시를 «글자로» 넣지 않는다
 *
 * 서버가 앞 80자로 **이미** 자른다. 거기에 `…` 을 붙이면 좁은 화면에서 CSS 말줄임이 **또**
 * 걸려 **「…」이 두 번** 보인다. 그리고 「80자에서 잘렸다」와 「칸이 좁아 잘렸다」는 사람에게
 * **같은 뜻**이라 구별할 값이 없다. ⇒ 자르는 것은 CSS 에 맡긴다.
 */
export type ChatPreviewKind = 'text' | 'attachment' | 'deleted' | 'unknown' | 'none';

export interface ChatPreview {
  text: string;
  kind: ChatPreviewKind;
}

export interface ChatPreviewOptions {
  /** 나. 내가 보낸 메시지면 이름 대신 **「나」** 를 쓴다. */
  myUserId?: string | null;
  /** 1:1 대화인가. 방 이름이 곧 상대 이름이라 **접두를 안 붙인다.** */
  isDirect?: boolean;
}

/**
 * 「누가 보냈나」를 앞에 붙인다 — 「김서연: 리뷰 부탁드립니다」 (2026-09-23 사용자 결정).
 *
 * **안 붙이는 경우가 넷**이고, 넷 다 **채팅 화면이 지금 그 사람을 뭐라고 부르는지**를 재서 맞췄다.
 *
 * ```
 *   1:1 대화     방 이름이 «곧 상대 이름»이라 「김서연」 방에 「김서연: …」 이면 이름이 두 번이다
 *                (칸은 좁은데 새 정보가 0)
 *   AI           ⚠ 사용자 결정 — 안 붙인다. AI 방은 방 이름이 이미 「업무 비서」라 같은 겹침이 난다
 *   이름이 없음   서버가 이름을 못 준 사람 — 「null: …」 을 만들지 않는다
 * ```
 *
 * ## ⚠ `senderId` 가 `null` 인 경우 — 「시스템 메시지」로 읽지 말 것
 *
 * 프런트에 `isSystemMessage`(= `senderId === null && !isAi`)라는 판정이 있어서 **그런 메시지가
 * 미리보기로 온다고 읽기 쉽다.** 2026-09-23 에 **관제와 백엔드가 각자** 서버의 쓰기 경로를
 * 전수로 세었고, 결과는 이렇다:
 * ```
 *   chat_messages 에 senderId=null 로 «쓰는» 곳 — 전부 is_ai / isAiResponse = true
 *     automation-room.ts:93 · messages.routes.ts:1440 · :1475 · :1544 · :1584 · 마이그 206
 *   (mail-import.ts:659 도 senderId=null 이지만 «mail_messages» 표다 — 채팅이 아니다)
 * ⇒ «시스템 전용 경로»가 없다
 * ```
 * ★ **「…님이 들어왔습니다」 같은 시스템 안내는 우리 코드에 없다**(2026-09-23 · 0건).
 *   방에 사람을 더할 때 남는 것은 **초대한 사람이 보낸 보통 메시지**다 —
 *   `rooms.routes.ts:1040` 이 `senderId: callerId` 로 「…님을 대화에 초대했어요.」를 넣는다.
 *   ⇒ 미리보기는 **「오영진: 김서연님을 대화에 초대했어요.」**로 그린다. **맞는 말이다.**
 *   ⇒ 미리보기 질의가 그것을 **안 거르는 것도 «의도»**다(`room-last-message-preview.ts:96`).
 *
 * ⚠ **그런데도 아래 `senderId == null` 줄을 «남긴다».** 「쓰는 경로가 없다」와 「그런 행이 없다」는
 *   **다른 말**이기 때문이다 — **옛 판이 만든 행이 DB 에 남아 있을 수 있고**, 코드를 다 세도
 *   **이미 저장된 것은 못 센다.** 그런 행에서 **「null: …」** 이 보이면 안 된다.
 *
 * ## ★ 붙일지 말지를 가르는 한 줄
 *
 * **그 사람이 «한 일»을 말하면 붙이고, 그 메시지의 «상태»를 말하면 안 붙인다.**
 * ```
 *   본문 · 「첨부파일」    그 사람이 «보낸» 것   ⇒ 붙인다   (「첨부파일」도 우리가 지은 말이지만
 *                                                       «파일을 보냈다»는 그 사람이 한 일이다)
 *   「지워진 메시지」 · 「—」  그 메시지의 «상태»   ⇒ 안 붙인다 (「나: 지워진 메시지」가 되면
 *                                                       그 사람이 그렇게 «말한» 것으로 읽힌다)
 * ```
 *
 * ## ★ 「나」로 적는 것은 «알고 고른» 값이다
 *
 * 채팅 화면은 내 메시지에 **내 실제 이름**을 쓴다(`MessageItem.tsx` · 줄 방향만 뒤집는다).
 * 그래서 이 목록과 채팅 화면이 **나를 두 이름으로 부른다** — 사용자가 **그 주의를 보고**
 * 「나」를 골랐다(2026-09-23). ⛔ **버그가 아니다. 되돌리지 마라.**
 * 같은 뜻에서 AI·시스템에 이름을 안 붙이면 **「사람이 쓴 말」과 구별이 덜 되는데**, 그것도
 * **알고 고른 값**이다.
 */
function senderPrefix(
  p: RoomLastMessagePreviewDto,
  opts: ChatPreviewOptions,
): string {
  if (opts.isDirect) return '';            // 1:1 — 방 이름이 곧 상대 이름
  if (p.isAi) return '';                   // AI — 지금 senderId 를 null 로 «쓰는» 경로는 이것뿐이다
  // ⚠ 옛 판이 남긴 행 방어 — 쓰는 경로는 없어도 «저장된 행»은 있을 수 있다(위 주석). 「null: 」 금지.
  if (p.senderId == null) return '';
  if (opts.myUserId != null && String(opts.myUserId) !== '' && p.senderId === String(opts.myUserId)) {
    return '나: ';
  }
  const name = (p.senderName ?? '').trim();
  return name === '' ? '' : `${name}: `;   // 이름을 못 받았으면 「null: 」을 만들지 않는다
}

export function chatPreview(
  p: RoomLastMessagePreviewDto | null | undefined,
  opts: ChatPreviewOptions = {},
): ChatPreview {
  // 칸 자체가 없다 = 서버가 못 읽었다. 「모른다」를 말한다 — 비워 두면 「첨부만」과 섞인다.
  if (p == null) return { text: '—', kind: 'unknown' };

  // 톰스톤이 먼저다 — 지워진 메시지는 text 가 null 이고 hasAttachments 도 false 다.
  if (p.deleted) return { text: '지워진 메시지', kind: 'deleted' };

  /*
   * ★ 접두는 **누가 «보낸» 것**에만 붙인다 — 본문과 「첨부파일」까지다.
   *   「지워진 메시지」와 「—」는 **우리가 적은 말**이라, 거기에 이름을 붙이면
   *   「나: 지워진 메시지」처럼 **그 사람이 그렇게 말한 것**으로 읽힌다.
   *   (화면이 그 둘을 기울임으로 갈라 두는 것과 같은 까닭이다.)
   */
  const who = senderPrefix(p, opts);

  const text = p.text ?? '';
  if (text === '') {
    // 글자가 없고 첨부가 있다 = 파일만 보낸 메시지.
    if (p.hasAttachments) return { text: `${who}첨부파일`, kind: 'attachment' };
    // 글자도 첨부도 없다 — 서버 모양상 오지 않지만, 오면 «지어내지 않고» 비운다.
    return { text: '', kind: 'none' };
  }
  return { text: `${who}${text}`, kind: 'text' };
}
